import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trash2, Plus } from "lucide-react";
import { updateProduct, fetchProductImages, addProductImage, deleteProductImage, fetchCategories } from "@/lib/api";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Product } from "@/lib/api";

interface EditProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: Product;
}

export const EditProductDialog = ({ open, onOpenChange, product }: EditProductDialogProps) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState(product.item_name);
  const [price, setPrice] = useState(product.price);
  const [imageUrl, setImageUrl] = useState(product.image_url || "");
  const [offerLink, setOfferLink] = useState(product.offer_link || "");
  const [productLink, setProductLink] = useState(product.product_link || "");
  const [description, setDescription] = useState(product.description || "");
  const [category, setCategory] = useState(product.category || "");
  const [subcategory, setSubcategory] = useState(product.subcategory || "");
  const [featured, setFeatured] = useState(product.featured || false);
  const [banner, setBanner] = useState(product.banner || false);
  const [national, setNational] = useState(product.national || false);
  const [newImageUrl, setNewImageUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const { data: categories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: fetchCategories,
  });

  const { data: allProducts = [] } = useQuery({
    queryKey: ["products"],
  });

  const subcategories = category
    ? [...new Set(
        (allProducts as Product[])
          .filter((p) => p.category === category && p.subcategory)
          .map((p) => p.subcategory!)
      )].sort()
    : [];

  const { data: extraImages = [], refetch: refetchImages } = useQuery({
    queryKey: ["product-images", product.id],
    queryFn: () => fetchProductImages(product.id),
    enabled: open,
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProduct(product.id, {
        item_name: name,
        price,
        image_url: imageUrl || null,
        offer_link: offerLink || null,
        product_link: productLink || null,
        description: description || null,
        category: category || null,
        subcategory: subcategory || null,
        featured,
        banner,
        national,
      });
      toast.success("Produto atualizado");
      queryClient.invalidateQueries({ queryKey: ["products"] });
      queryClient.invalidateQueries({ queryKey: ["product", product.id] });
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      onOpenChange(false);
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  const handleAddImage = async () => {
    if (!newImageUrl.trim()) return;
    try {
      await addProductImage(product.id, newImageUrl.trim(), extraImages.length);
      setNewImageUrl("");
      refetchImages();
      queryClient.invalidateQueries({ queryKey: ["product-images", product.id] });
    } catch {
      toast.error("Erro ao adicionar imagem");
    }
  };

  const handleDeleteImage = async (id: string) => {
    try {
      await deleteProductImage(id);
      refetchImages();
      queryClient.invalidateQueries({ queryKey: ["product-images", product.id] });
    } catch {
      toast.error("Erro ao remover imagem");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Produto</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Left column */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Nome</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>

            <div className="space-y-1">
              <Label>Preço</Label>
              <Input value={price} onChange={(e) => setPrice(e.target.value)} />
            </div>

            <div className="space-y-1">
              <Label>Categoria</Label>
              <Select value={category || "__none__"} onValueChange={(v) => { setCategory(v === "__none__" ? "" : v); setSubcategory(""); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhuma</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Ou digite nova..." className="mt-1" />
            </div>

            <div className="space-y-1">
              <Label>Subcategoria</Label>
              <Select value={subcategory || "__none__"} onValueChange={(v) => setSubcategory(v === "__none__" ? "" : v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecionar..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nenhuma</SelectItem>
                  {subcategories.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input value={subcategory} onChange={(e) => setSubcategory(e.target.value)} placeholder="Ou digite nova..." className="mt-1" />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <input
                type="checkbox"
                id="featured"
                checked={featured}
                onChange={(e) => setFeatured(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <Label htmlFor="featured" className="cursor-pointer">Produto em destaque</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="banner"
                checked={banner}
                onChange={(e) => setBanner(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <Label htmlFor="banner" className="cursor-pointer">Exibir no banner rotativo</Label>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="national"
                checked={national}
                onChange={(e) => setNational(e.target.checked)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <Label htmlFor="national" className="cursor-pointer">🇧🇷 Produto Nacional</Label>
            </div>

            <div className="space-y-1">
              <Label>Link de oferta</Label>
              <Input value={offerLink} onChange={(e) => setOfferLink(e.target.value)} placeholder="https://..." />
            </div>

            <div className="space-y-1">
              <Label>Link do produto</Label>
              <Input value={productLink} onChange={(e) => setProductLink(e.target.value)} placeholder="https://..." />
            </div>
          </div>

          {/* Right column */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Descrição</Label>
              <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Descrição do produto..." rows={3} />
            </div>

            <div className="space-y-1">
              <Label>Imagem principal (URL)</Label>
              <Input value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="https://..." />
              {imageUrl && (
                <img src={imageUrl} alt="Preview" className="mt-1 h-16 w-16 rounded object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
              )}
            </div>

            {/* Extra images */}
            <div className="space-y-2">
              <Label>Imagens adicionais</Label>
              {extraImages.map((img) => (
                <div key={img.id} className="flex items-center gap-2">
                  <img src={img.image_url} alt="" className="h-10 w-10 rounded object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                  <span className="flex-1 truncate text-xs text-muted-foreground">{img.image_url}</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteImage(img.id)}>
                    <Trash2 className="h-3 w-3 text-destructive" />
                  </Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="URL da nova imagem"
                  onKeyDown={(e) => e.key === "Enter" && handleAddImage()}
                />
                <Button variant="outline" size="icon" className="h-9 w-9" onClick={handleAddImage}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
